import { defineList, type FieldsOf } from "./define";

/**
 * Schema dell'app "Richieste di manutenzione".
 * Nomi interni ASCII senza accenti; le etichette visibili possono averli.
 *
 * Versione 2 (ottobre 2026): sedi, servizi, categorie e servizi per sede diventano
 * elenchi gestiti dai gestori. Gli elenchi sono propri di Manutenzioni: l'elenco
 * "Servizi" usato da Acquisti non viene più letto né modificato.
 *
 * Versione 3 (ottobre 2026): elenco dei fornitori e fornitore scelto dal gestore.
 */

export const STATI = ["Sottomessa", "Presa visione", "In lavorazione", "Bloccata", "Respinta", "Evasa"] as const;
export type Stato = (typeof STATI)[number];

/** Stati che chiudono la richiesta (valorizzano DataChiusura). */
export const STATI_CHIUSI: readonly Stato[] = ["Evasa", "Respinta"];

/**
 * Categorie della prima versione. Restano come scelte della colonna Categoria per le
 * richieste già inviate; le categorie proposte nel modulo vengono dall'elenco
 * "Categorie manutenzione" (la colonna accetta valori fuori elenco).
 */
export const CATEGORIE_V1 = [
  "Edilizia",
  "Idraulica",
  "Energia (Elettricità, Gas)",
  "Arredamento",
  "Terreni",
  "Mista (edilizia + idraulica)",
  "Altro",
] as const;

export const PRIORITA = ["Standard", "Urgente"] as const;

/* -------------------------------------------------------------------------- */
/* Elenchi di configurazione (gestiti dai gestori)                             */
/* -------------------------------------------------------------------------- */

const configColumns = [
  { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  { kind: "number", name: "Ordine", displayName: "Ordine", decimalPlaces: "none" },
] as const;

const seedValori = (valori: readonly string[]) => valori.map((Title, i) => ({ Title, Attivo: true, Ordine: (i + 1) * 10 }));

export const SediManutenzione = defineList({
  key: "sedi",
  displayName: "Sedi manutenzione",
  description: "Sedi per cui si possono chiedere interventi (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Sede",
  columns: configColumns,
  seed: seedValori(["PASCOLETTO", "POLESINE", "VIA DEL PARCO", "VIA DEMOCRITO"]),
});
export type SedeFields = FieldsOf<typeof SediManutenzione>;

export const ServiziManutenzione = defineList({
  key: "servizi",
  displayName: "Servizi manutenzione",
  description: "Servizi tra cui scegliere nelle richieste di manutenzione (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Servizio",
  columns: configColumns,
  seed: seedValori([
    "BISTROZZI",
    "CDD",
    "COMUNITÀ",
    "CSE",
    "DIURNO MINORI",
    "HOUSING",
    "LOCALI TECNICI",
    "MACCHINETTE",
    "PASCOLETTO",
    "SFA",
    "UFFICI",
  ]),
});
export type ServizioFields = FieldsOf<typeof ServiziManutenzione>;

export const CategorieManutenzione = defineList({
  key: "categorie",
  displayName: "Categorie manutenzione",
  description: "Categorie di intervento (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Categoria",
  columns: configColumns,
  seed: seedValori([
    "ANTINCENDIO",
    "ARREDAMENTO-FORNITURE",
    "ASCENSORI",
    "CLIMA-RISCALDAMENTO",
    "DISINFESTAZIONE",
    "EDILIZIA",
    "ELETTRICO (LUCE)",
    "ELETTRODOMESTICI",
    "IDRAULICA",
    "IMBIANCHINO",
    "MACCHINETTE",
    "MEZZI",
    "PULIZIE STRAORDINARIE",
    "SPURGO",
    "VERDE",
  ]),
});
export type CategoriaFields = FieldsOf<typeof CategorieManutenzione>;

/**
 * Quali servizi ci sono in ogni sede e, dove servono, le aree interne.
 * - Una riga senza Area dice che il servizio è presente nella sede.
 * - Le righe con Area elencano le scelte del campo "Area" per quella sede e servizio
 *   (es. VIA DEL PARCO · HOUSING · OSTELLO); se esistono, il campo compare ed è obbligatorio.
 * - Una sede senza righe mostra tutti i servizi (utile finché l'elenco non è compilato).
 */
export const ServiziPerSede = defineList({
  key: "serviziPerSede",
  displayName: "Servizi per sede",
  description: "Servizi presenti in ogni sede e relative aree (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Descrizione",
  columns: [
    { kind: "lookup", name: "Sede", displayName: "Sede", list: "sedi", required: true },
    { kind: "lookup", name: "Servizio", displayName: "Servizio", list: "servizi", required: true },
    { kind: "text", name: "Area", displayName: "Area", description: "Facoltativa: es. OSTELLO, APP. NUOVI" },
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  ],
});
export type ServizioPerSedeFields = FieldsOf<typeof ServiziPerSede>;

/**
 * Fornitori per le manutenzioni (v3). Li vede e li sceglie solo il gestore.
 * Un fornitore è proposto per una richiesta se:
 *  - la categoria della richiesta è tra le sue Categorie;
 *  - la sede è tra le sue Sedi, oppure Sedi è vuoto (= tutte le sedi);
 *  - il servizio è tra i suoi Servizi, oppure Servizi è vuoto (= tutti i servizi).
 * Gli altri restano selezionabili con "Mostra tutti i fornitori".
 */
export const FornitoriManutenzione = defineList({
  key: "fornitori",
  displayName: "Fornitori manutenzione",
  description: "Fornitori da incaricare per gli interventi (gestito dai gestori, non visibile ai coordinatori)",
  template: "genericList",
  titleLabel: "Fornitore",
  columns: [
    { kind: "text", name: "Referente", displayName: "Referente" },
    { kind: "text", name: "Telefono", displayName: "Telefono" },
    { kind: "text", name: "Email", displayName: "E-mail" },
    { kind: "lookup", name: "Categorie", displayName: "Categorie", list: "categorie", allowMultiple: true },
    {
      kind: "lookup",
      name: "Sedi",
      displayName: "Sedi",
      list: "sedi",
      allowMultiple: true,
      description: "Vuoto = tutte le sedi",
    },
    {
      kind: "lookup",
      name: "Servizi",
      displayName: "Servizi",
      list: "servizi",
      allowMultiple: true,
      description: "Vuoto = tutti i servizi",
    },
    { kind: "note", name: "Note", displayName: "Note" },
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  ],
});
export type FornitoreFields = FieldsOf<typeof FornitoriManutenzione>;

/* -------------------------------------------------------------------------- */
/* Richieste                                                                   */
/* -------------------------------------------------------------------------- */

export const RichiesteManutenzione = defineList({
  key: "richieste",
  // Nome diverso dall'elenco "Richieste manutenzione" della vecchia app Power Apps,
  // che resta sullo stesso sito finché non viene dismessa.
  displayName: "Richieste interventi",
  description: "Richieste di intervento manutentivo inviate dai coordinatori delle strutture",
  template: "genericList",
  titleLabel: "Titolo",
  // "Created" serve per ordinare dalla più recente anche oltre 5.000 elementi
  indexBuiltIn: ["Created"],
  columns: [
    // --- compilati dal richiedente ---
    { kind: "text", name: "Sede", displayName: "Sede", description: "Valore preso dall'elenco Sedi manutenzione", indexed: true },
    {
      kind: "text",
      name: "Servizio",
      displayName: "Servizio",
      description: "Valore preso dall'elenco Servizi manutenzione",
      required: true,
      indexed: true,
    },
    { kind: "text", name: "Area", displayName: "Area", description: "Solo dove prevista (es. HOUSING): dall'elenco Servizi per sede" },
    {
      kind: "choice",
      name: "Categoria",
      displayName: "Categoria",
      choices: CATEGORIE_V1,
      // dalla v2 i valori vengono dall'elenco Categorie manutenzione
      allowTextEntry: true,
      required: true,
    },
    { kind: "note", name: "Descrizione", displayName: "Descrizione", required: true },
    {
      kind: "choice",
      name: "Priorita",
      displayName: "Priorità",
      choices: PRIORITA,
      defaultValue: "Standard",
      indexed: true,
    },
    // --- solo richieste della prima versione: sede scritta a mano ---
    { kind: "text", name: "Struttura", displayName: "Struttura (v1)", description: "Sede in testo libero, solo richieste v1" },
    // --- automatici ---
    { kind: "person", name: "Richiedente", displayName: "Richiedente", indexed: true },
    { kind: "dateTime", name: "DataChiusura", displayName: "Data chiusura", format: "dateTime" },
    { kind: "person", name: "GestitoDa", displayName: "Gestito da" },
    // --- compilati dal gestore ---
    {
      kind: "choice",
      name: "Stato",
      displayName: "Stato",
      choices: STATI,
      defaultValue: "Sottomessa",
      required: true,
      indexed: true,
    },
    { kind: "number", name: "Quantita", displayName: "Quantità", decimalPlaces: "automatic", min: 0 },
    { kind: "currency", name: "Valore", displayName: "Valore", locale: "it-IT" },
    { kind: "boolean", name: "ExtraBudget", displayName: "Extra budget", defaultValue: false },
    { kind: "note", name: "NoteGestore", displayName: "Note gestore" },
    // v3: scelto dal gestore, non mostrato al richiedente
    { kind: "lookup", name: "Fornitore", displayName: "Fornitore", list: "fornitori" },
  ],
});
export type RichiestaFields = FieldsOf<typeof RichiesteManutenzione>;

export const FotoRichieste = defineList({
  key: "foto",
  displayName: "Foto richieste",
  description: "Foto allegate alle richieste di manutenzione (una cartella per richiesta)",
  template: "documentLibrary",
  columns: [
    { kind: "number", name: "RichiestaId", displayName: "ID richiesta", decimalPlaces: "none", indexed: true },
  ],
});
export type FotoFields = FieldsOf<typeof FotoRichieste>;

/** Tutte le strutture dati dell'app, nell'ordine di provisioning (i lookup dopo i loro elenchi). */
export const appSchema = [
  SediManutenzione,
  ServiziManutenzione,
  CategorieManutenzione,
  ServiziPerSede,
  FornitoriManutenzione,
  RichiesteManutenzione,
  FotoRichieste,
] as const;
